import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { firebaseConfigured } from "@/lib/firebase";
import { DUR_SETTLE, EASE_PRODUCT } from "@/lib/motion";

export default function LoginPage() {
  const { signInWithGoogle, signInWithPassword } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mostrarFallback, setMostrarFallback] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setEntrando(true);
    setErro(null);
    const { error } = await signInWithPassword(email, senha);
    setEntrando(false);
    if (error) setErro(error);
  }

  return (
    <div className="login-screen">
      <div className="login-glow" aria-hidden="true" />
      <button
        type="button"
        className="btn btn--ghost btn--icon btn--sm login-theme-toggle"
        onClick={toggleTheme}
        aria-label={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
        title="Alternar tema"
      >
        {theme === "dark" ? (
          <Sun className="icon" size={18} aria-hidden="true" />
        ) : (
          <Moon className="icon" size={18} aria-hidden="true" />
        )}
      </button>
      <motion.div
        className="login-card"
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: DUR_SETTLE, ease: EASE_PRODUCT }}
      >
        <div className="login-mark">
          <img src="/logos/cafelabs.svg" alt="" width={32} height={32} />
        </div>
        <h1 style={{ marginBottom: 2 }}>Café Labs Admin</h1>
        <p className="field-hint" style={{ marginBottom: 20 }}>
          Acesso restrito à equipe da Café Labs.
        </p>

        {!firebaseConfigured && (
          <p className="field-error" role="alert">
            Configuração do Firebase ausente (VITE_FIREBASE_*). Veja
            docs/backend-setup.md.
          </p>
        )}

        <button type="button" className="btn btn--primary btn--block" onClick={() => void signInWithGoogle()}>
          Entrar com Google
        </button>

        <div className="login-divider">ou</div>

        <button
          type="button"
          className="btn btn--secondary btn--block"
          onClick={() => setMostrarFallback((v) => !v)}
          aria-expanded={mostrarFallback}
        >
          {mostrarFallback ? "Ocultar e-mail e senha" : "E-mail e senha"}
        </button>

        {mostrarFallback && (
          <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
            <div className="field">
              <label htmlFor="login-email">E-mail</label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="login-senha">Senha</label>
              <input
                id="login-senha"
                type="password"
                required
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </div>
            {erro && (
              <p className="field-error" role="alert">
                {erro}
              </p>
            )}
            <button type="submit" className="btn btn--secondary btn--block" disabled={entrando}>
              {entrando ? "Entrando…" : "Entrar"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
